import clsx from 'clsx';
import type { ReactNode } from 'react';
import { InfoButton } from '@/components/InfoButton';

type Tone = 'default' | 'success' | 'danger' | 'warning' | 'brand';

const TONE_STYLES: Record<Tone, { value: string; icon: string }> = {
  default: { value: 'text-slate-100', icon: 'bg-slate-800 text-slate-400' },
  brand: { value: 'text-brand-300', icon: 'bg-brand-500/10 text-brand-400' },
  success: { value: 'text-emerald-300', icon: 'bg-emerald-500/10 text-emerald-400' },
  danger: { value: 'text-red-300', icon: 'bg-red-500/10 text-red-400' },
  warning: { value: 'text-amber-300', icon: 'bg-amber-500/10 text-amber-400' },
};

export function StatCard({
  label,
  value,
  sublabel,
  icon,
  tone = 'default',
  loading,
  info,
}: {
  label: string;
  value: ReactNode;
  sublabel?: ReactNode;
  icon?: ReactNode;
  tone?: Tone;
  loading?: boolean;
  /** Explanation of exactly what this figure counts. */
  info?: ReactNode;
}) {
  const styles = TONE_STYLES[tone];

  return (
    <div className="card p-5">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="flex items-center gap-1.5 text-xs font-medium uppercase tracking-wider text-slate-500">
            {label}
            {info ? <InfoButton label={label}>{info}</InfoButton> : null}
          </p>
          {loading ? (
            <div className="skeleton mt-2 h-8 w-20" />
          ) : (
            <p className={clsx('mt-1.5 text-2xl font-semibold tabular-nums tracking-tight', styles.value)}>{value}</p>
          )}
          {sublabel && !loading ? <p className="mt-1 truncate text-xs text-slate-500">{sublabel}</p> : null}
        </div>
        {icon ? (
          <div className={clsx('flex h-9 w-9 shrink-0 items-center justify-center rounded-lg', styles.icon)}>{icon}</div>
        ) : null}
      </div>
    </div>
  );
}
